import { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import BrandLogo from '@/components/BrandLogo'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { useTheme } from '@/lib/hooks'

import {
  TopBarActions,
  TopBarAvatar,
  TopBarBrandPrefix,
  TopBarCashier,
  TopBarCashierName,
  TopBarClock,
  TopBarClosing,
  TopBarDiagButton,
  TopBarDivider,
  TopBarLoggedIn,
  TopBarLogo,
  TopBarWrapper
} from './TopBar.styles'

const UserIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
)

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
)

const GearIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
)

const TopBar = () => {
  const { t, i18n } = useTranslation()
  const themes = useTheme()
  const navigate = useNavigate()
  const [time, setTime] = useState(moment())

  const initialMinutes = useMemo(() => Math.floor(Math.random() * 180) + 30, [])
  const [loggedMinutes, setLoggedMinutes] = useState(initialMinutes)

  const customerWindowRef = useRef(null)
  const logoClickCount = useRef(0)
  const logoClickTimer = useRef(null)

  const handleLogoClick = () => {
    logoClickCount.current += 1

    if (logoClickTimer.current) clearTimeout(logoClickTimer.current)
    logoClickTimer.current = setTimeout(() => { logoClickCount.current = 0 }, 3000)

    if (logoClickCount.current >= 5) {
      logoClickCount.current = 0
      if (window.BtDevice?.clearSettings) {
        window.BtDevice.clearSettings()
      }
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(moment())
      setLoggedMinutes(prev => prev + 1)
    }, 60000)
    const clockInterval = setInterval(() => setTime(moment()), 1000)
    return () => {
      clearInterval(interval)
      clearInterval(clockInterval)
    }
  }, [])

  const openCustomerDisplay = () => {
    const params = new URLSearchParams(window.location.search)
    const customerUrl = `${window.location.origin}/customer?${params.toString()}`

    if (window.AndroidBridge?.openCustomerDisplay) {
      window.AndroidBridge.openCustomerDisplay(customerUrl)
    } else {
      if (customerWindowRef.current && !customerWindowRef.current.closed) {
        customerWindowRef.current.focus()
      } else {
        customerWindowRef.current = window.open(customerUrl, 'customer-display', 'width=800,height=1280')
      }
    }
  }

  const hours = Math.floor(loggedMinutes / 60)
  const mins = loggedMinutes % 60
  const loggedInText = t('topBar.loggedIn', { time: hours > 0 ? `${hours}h ${mins}m` : `${mins}m` })

  const closingHour = 20 // store closes at 20:00
  const now = time
  const closingTime = moment(now).hour(closingHour).minute(0).second(0)
  const minutesLeft = closingTime.diff(now, 'minutes')
  const closingText = minutesLeft > 0
    ? t('topBar.closesIn', { time: `${Math.floor(minutesLeft / 60)}h ${minutesLeft % 60}m` })
    : t('topBar.storeClosed')
  const localTime = moment(time).locale(i18n.language)

  return (
    <TopBarWrapper>
      <TopBarLogo onClick={handleLogoClick}>
        {themes.brandPrefix && <TopBarBrandPrefix>{t('topBar.poweredBy')}</TopBarBrandPrefix>}
        <BrandLogo size={34} />
      </TopBarLogo>
      <TopBarDivider />
      <TopBarCashier>
        <TopBarAvatar><UserIcon /></TopBarAvatar>
        <div>
          <TopBarCashierName>{t('topBar.cashier')}</TopBarCashierName>
          <TopBarLoggedIn>{loggedInText}</TopBarLoggedIn>
        </div>
      </TopBarCashier>
      <TopBarActions>
        <TopBarClosing $warning={minutesLeft <= 60 && minutesLeft > 0} $closed={minutesLeft <= 0}>
          <ClockIcon />
          {closingText}
        </TopBarClosing>
        <LanguageSwitcher />
        <TopBarDiagButton
          type="button"
          onClick={() => navigate('/diagnostics')}
          title={t('topBar.diagnostics')}
          aria-label={t('topBar.diagnostics')}
        >
          <GearIcon />
        </TopBarDiagButton>
      </TopBarActions>
      <TopBarDivider />
      <TopBarClock>
        <div className="time">{localTime.format('HH:mm')}</div>
        <div className="date">{localTime.format('dddd')}, {localTime.format(t('topBar.dateFormat'))}</div>
      </TopBarClock>
    </TopBarWrapper>
  )
}

export default TopBar
